import { UtilsService } from '../../services/utils.service';
import {
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  MY_CAL_FILTER_OPTION_KEYS,
  MyCalOption,
  SYNC_SYSTEM_OPTION_KEYS,
  SyncSystemOption,
} from '../../types/my-calendar.type';
import {
  EventResponseEvent,
  myCalendarShowOptions,
} from 'src/app/types/event-response.type';
import { Observable, Subscription } from 'rxjs';
import { AuthService, User } from 'src/app/services/auth.service';
import { isAfter, isSameDay } from 'date-fns';
import { CalendarHttpService } from 'src/app/services/calendar.http.service';
import { UsersService } from '../../services/users.service';
import { BREAKPOINT_MD } from '../../constants/constants';

@Component({
  selector: 'app-my-calendar',
  templateUrl: './my-calendar.component.html',
  styleUrls: ['./my-calendar.component.scss'],
})
export class MyCalendarComponent implements OnInit, OnDestroy {
  constructor(
    private calendarHttpService: CalendarHttpService,
    public authService: AuthService,
    public usersService: UsersService,
    private cdr: ChangeDetectorRef
  ) {}

  public user$: Observable<User | null> = this.authService.user$;
  public dateSelected: Date = new Date();
  public myEventsFiltered: EventResponseEvent[] = [];
  public myEvents: EventResponseEvent[] = [];
  public selectedEvent: EventResponseEvent | null = null;
  private myCalendarShowOptions: any | undefined;
  private subscriptions: Subscription = new Subscription();
  private innerWidth: number = window.innerWidth;

  @HostListener('window:resize')
  onResize(): void {
    this.innerWidth = window.innerWidth;
  }

  get showActiveEventInline(): boolean {
    return this.innerWidth < BREAKPOINT_MD;
  }

  private options: MyCalOption[] = [
    {
      key: MY_CAL_FILTER_OPTION_KEYS.EVENTS,
      title: 'Liked events',
      is_selected: true,
    },
    {
      key: MY_CAL_FILTER_OPTION_KEYS.ORGANIZATIONS,
      title: 'Followed organizations',
      is_selected: true,
    },
    {
      key: MY_CAL_FILTER_OPTION_KEYS.SPORTS,
      title: 'Followed sports',
      is_selected: true,
    },
    // {
    // key: MY_CAL_FILTER_OPTION_KEYS.NEAR_ME,
    // title: 'Near to me',
    // is_selected: true,
    // },
  ];

  public showOptions: MyCalOption[] = [...this.options];
  public syncOptions: MyCalOption[] = [...this.options];

  public syncSystemOptions: SyncSystemOption[] = [
    {
      key: SYNC_SYSTEM_OPTION_KEYS.GOOGLE,
      title: 'Google calendar',
    },
    {
      key: SYNC_SYSTEM_OPTION_KEYS.YAHOO,
      title: 'Yahoo calendar',
    },
    {
      key: SYNC_SYSTEM_OPTION_KEYS.OUTLOOK,
      title: 'Outlook',
    },
    {
      key: SYNC_SYSTEM_OPTION_KEYS.APPLE_CALENDAR,
      title: 'Apple calendar',
    },
    {
      key: SYNC_SYSTEM_OPTION_KEYS.ICAL,
      title: 'iCal',
    },
  ];

  public openStates = {
    sync: false,
    preferences: false,
    showOptions: false,
  };

  selectEvent(event: EventResponseEvent | null): void {
    this.selectedEvent = event;
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.user$.subscribe((user) => {
        if (!user) {
          return;
        }

        try {
          this.myCalendarShowOptions = JSON.parse(
            UtilsService.getSetting('myCalendarShowOptions', user) ?? ''
          );
        } catch {
          this.myCalendarShowOptions = undefined;
        }

        this.showOptions.forEach((option) => {
          if (
            this.myCalendarShowOptions &&
            option.key in this.myCalendarShowOptions
          ) {
            option.is_selected = this.myCalendarShowOptions[option.key];
          }
        });

        this.getEvents();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getEvents(): void {
    if (!this.authService.user?.id) {
      return;
    }

    const getEventsParams = {
      userLikedEvents: true,
      userFollowedOrganizations: true,
      userFollowedSports: true,
    };

    this.showOptions.forEach((option) => {
      if (
        this.myCalendarShowOptions &&
        option.key in this.myCalendarShowOptions
      ) {
        const isSelected = this.myCalendarShowOptions[option.key];

        switch (option.key) {
          case MY_CAL_FILTER_OPTION_KEYS.EVENTS:
            getEventsParams.userLikedEvents = isSelected;
            break;
          case MY_CAL_FILTER_OPTION_KEYS.ORGANIZATIONS:
            getEventsParams.userFollowedOrganizations = isSelected;
            break;
          case MY_CAL_FILTER_OPTION_KEYS.SPORTS:
            getEventsParams.userFollowedSports = isSelected;
            break;
        }
      }
    });

    const hasGetEventsParams = Object.values(getEventsParams).some(
      (val) => val
    );

    if (!hasGetEventsParams) {
      this.myEvents = [];
      this.selectEvent(null);
      this.myEventsFiltered = [];
      this.cdr.detectChanges();
      return;
    }

    this.calendarHttpService
      .getEvents({
        userId: this.authService.user.id,
        ...getEventsParams,
      })
      .subscribe((events) => {
        this.myEvents = events;
        this.myEventsFiltered = this.getMyEventsFiltered();

        const targetEvent = this.myEventsFiltered[0] ?? null;
        this.selectEvent(targetEvent);

        this.cdr.detectChanges();
      });
  }

  myCalendarShowOptionsChanged(formData: myCalendarShowOptions): void {
    this.myCalendarShowOptions = formData;
    this.usersService.saveUserSetting(
      'myCalendarShowOptions',
      JSON.stringify(formData)
    );

    this.getEvents();
  }

  getMyEventsFiltered(): EventResponseEvent[] {
    const myEvents = this.myEvents.filter((event) => {
      const eventStartDate = new Date(event.event_start);

      return (
        isSameDay(eventStartDate, this.dateSelected) ||
        isAfter(eventStartDate, this.dateSelected)
      );
    });

    return myEvents;
  }

  handleDayClicked(date: Date): void {
    this.dateSelected = date;
    this.myEventsFiltered = this.getMyEventsFiltered();

    const targetEvent = this.myEventsFiltered[0] ?? null;
    this.selectEvent(targetEvent);
  }
}
